// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { ApiError, createGraniteClient, type GraniteClient } from "@granite/api-client";
import { QueryClient } from "@tanstack/react-query";

import { accessToken, refresh, webClientOptions } from "./session.js";

/** The API client for signed-in calls: attaches the access token and keeps it fresh. */
export const api: GraniteClient = createGraniteClient({ ...webClientOptions, getAccessToken: accessToken });

api.use({
  async onResponse({ request, response }) {
    // The token was revoked or expired early: try the refresh cookie once. A read is sent again
    // with the new token right here, so queries that never retry don't keep the 401 (and show
    // "you can't" when the session had only expired); a write is left to its caller.
    if (response.status !== 401 || !(await refresh())) return undefined;
    if (request.method !== "GET" && request.method !== "HEAD") return undefined;
    const token = await accessToken();
    const again = new Request(request);
    if (token) again.headers.set("Authorization", `Bearer ${token}`);
    return fetch(again);
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry an unauthenticated request once (after the refresh above); never retry other
      // client errors; retry server and network errors twice.
      retry: (count, err) => {
        if (err instanceof ApiError) return err.status === 401 ? count < 1 : err.status >= 500 && count < 2;
        return count < 2;
      },
      staleTime: 30_000,
    },
  },
});
